import axios from 'axios';
import { apiBaseUrls, config } from '@/config';
import { useAuthStore } from '@/features/auth/store';
import { isAuthFailureEnvelope } from '@/features/auth/constants';
import { useEnvironmentStore } from '@/stores/environment';
/**
 * Pre-configured axios instance.
 * All API calls should use this instance, not the raw axios default.
 *
 * Interceptors:
 *  - Request: selects the base URL from the runtime environment store, then
 *    attaches the CONNECT accessToken as Authorization: Bearer <token>
 *  - Response: clears auth and redirects to /login on 401 or 403
 */
export const apiClient = axios.create({
    baseURL: config.apiBaseUrl,
    timeout: 15_000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
});
// Request interceptor — pick the target environment at runtime (login dropdown),
// then attach the CONNECT access token from the auth store.
apiClient.interceptors.request.use((requestConfig) => {
    requestConfig.baseURL = apiBaseUrls[useEnvironmentStore.getState().env];
    const token = useAuthStore.getState().accessToken;
    if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
}, (error) => Promise.reject(error));
// Response interceptor — the backend is the authoritative admin gate, so any
// auth rejection clears the session and returns to /login. This API signals auth
// failure two ways: real HTTP 401/403, AND (more commonly) HTTP 200 with an
// envelope { success:false, errorCode:"AUTH..." }. Both are handled here.
//
// Auth endpoints (e.g. login-social) are exempt from the envelope check: the
// LoginPage surfaces its own sign-in error there, and a redirect would wipe it.
apiClient.interceptors.response.use((response) => {
    const url = response.config.url ?? '';
    if (!url.includes('/auth/') && isAuthFailureEnvelope(response.data)) {
        useAuthStore.getState().clearAuth();
        window.location.replace('/login');
        return Promise.reject(new Error('Session expired. Please sign in again.'));
    }
    return response;
}, (error) => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
            useAuthStore.getState().clearAuth();
            window.location.replace('/login');
        }
    }
    return Promise.reject(error);
});
